import { StreamClient } from '../client/stream/StreamClient';
import { StreamColumnJsonModel, StreamJsonModel } from '../domain/stream/stream/StreamDomain';
import { query } from '@trlogic/client-js';
import MessageUtil from '../util/MessageUtil';
import { EnrichedComponentCategory } from '../domain/category/CategoryOperationDomains';
import _ from 'lodash';
import ComponentName from '../constant/ComponentName';
import GatewayContainer from '../config/GatewayContainer';
import FolderClient from '../client/folder/FolderClient';
import GatewayNames from '../constant/GatewayNames';
import { dispatch } from '../redux/store';
import { openBulkActionDialogFromDependency } from '../redux/actions/BulkActionActions';
import ExpressionBridge from '../util/ExpressionBridge';
import { Syntax } from '@trlogic/ui-pack';
import { changeStreamDirty } from '../redux/actions/StreamActions';
import { ActionType } from '../constant/Constants';
import Query = query.Query;
import LanguageUtil from '../util/LanguageUtil';
import DeployErrorUtil from '../util/DeployErrorUtil';
import { BulkActionDependencyError } from 'redux/reducers/BulkActionReducer';
import { ExpressionUtil } from '../util/ExpressionUtil';

class StreamService {
  private readonly expressionBridge: ExpressionBridge;

  constructor(expressionBridge: ExpressionBridge) {
    this.expressionBridge = expressionBridge;
  }

  private readonly streamClient: StreamClient = GatewayContainer.getInstance().get(GatewayNames.STREAM_CLIENT);
  private folderClient: FolderClient = GatewayContainer.getInstance().get(GatewayNames.FOLDER_CLIENT);

  public async save(_stream: StreamJsonModel, isUpdate: boolean, folder: EnrichedComponentCategory): Promise<boolean> {
    try {
      const stream: StreamJsonModel = _.cloneDeep(_stream);
      if (stream.name == undefined || stream.name == '') {
        MessageUtil.error({
          summary: LanguageUtil.get({ id: 'common.label.store', defaultMessage: 'Store' }),
          detail: LanguageUtil.get({ id: 'messages.error.withoutName', defaultMessage: 'You can not save without name' }),
        });
        return false;
      }
      if (ExpressionUtil.isEmpty(stream.key)) {
        MessageUtil.error({
          summary: LanguageUtil.get({ id: 'common.label.store', defaultMessage: 'Store' }),
          detail: LanguageUtil.get({ id: 'messages.error.emptyKeyExpression', defaultMessage: 'Key expression can not be empty' }),
        });
        return false;
      }
      if (this.warnIfColumnsMissing(stream.columns)) {
        return false;
      }
      const isExist = await this.streamClient.isExist(stream.name);
      if (isExist && !isUpdate) {
        MessageUtil.alreadyExist(LanguageUtil.get({ id: 'messages.warning.storeAlready', defaultMessage: 'Store already exist' }));
        return false;
      }
      if (!isUpdate && folder) {
        await this.folderClient.addItemToCategory(ComponentName.STREAM, folder.id, stream.name);
      }
      stream.columns = await Promise.all(
        stream.columns.map(async (c: StreamColumnJsonModel) => {
          return {
            ...c,
            expression: await this.parseExpression(c.expression!),
          };
        }),
      );
      stream.where = await this.parseExpression(stream.where);
      stream.key = await this.parseExpression(stream.key);
      await this.streamClient.save(stream);
      MessageUtil.success({
        summary: LanguageUtil.get({ id: 'common.label.store', defaultMessage: 'Store' }),
        detail: LanguageUtil.get({ id: 'messages.success.storeSaved' }),
      });
      dispatch(changeStreamDirty(false));
      return true;
    } catch (e: unknown) {
      if (this.isColumnsEmptyError(e)) {
        this.showColumnsEmpty();
        return false;
      }
      MessageUtil.error({
        summary: LanguageUtil.get({ id: 'common.label.store', defaultMessage: 'Store' }),
        detail: LanguageUtil.get({ id: 'messages.error.storeSaved' }),
      });
      return false;
    }
  }

  public async delete(id: string): Promise<boolean> {
    try {
      const response = await this.streamClient.delete(id);
      if (response.code) {
        return false;
      }
      MessageUtil.success({ content: LanguageUtil.get({ id: 'messages.success.deletedStore' }) });
      return true;
    } catch (e: any) {
      if (DeployErrorUtil.show(e, LanguageUtil.get({ id: 'common.label.store', defaultMessage: 'Store' }))) {
        return false;
      }
      const dependencies: any = e.response.data;
      if (dependencies?.message == 'Dependency Exception') {
        dispatch(openBulkActionDialogFromDependency(dependencies, ComponentName.STREAM));
        return false;
      }
      MessageUtil.error({ content: LanguageUtil.get({ id: 'messages.error.deletedStore' }) });
      return false;
    }
  }

  public async deploy(stream: StreamJsonModel): Promise<boolean> {
    if (Array.isArray(stream.columns) && !this.hasColumnContent(stream.columns)) {
      this.showColumnsEmpty();
      return false;
    }
    try {
      await this.streamClient.deploy(stream.name);
      MessageUtil.success({
        summary: LanguageUtil.get({ id: 'common.label.store', defaultMessage: 'Store' }),
        detail: LanguageUtil.get({ id: 'messages.success.deployStore' }),
      });
      return true;
    } catch (error: unknown) {
      if (this.isColumnsEmptyError(error)) {
        this.showColumnsEmpty();
        return false;
      }
      if (DeployErrorUtil.show(error, LanguageUtil.get({ id: 'common.label.store', defaultMessage: 'Store' }))) {
        return false;
      }
      MessageUtil.error({
        summary: LanguageUtil.get({ id: 'common.label.store', defaultMessage: 'Store' }),
        detail: LanguageUtil.get({ id: 'messages.error.deployStore' }),
      });
      return false;
    }
  }

  public async undeploy(id: string): Promise<boolean> {
    try {
      await this.streamClient.undeploy(id);
      MessageUtil.success({
        summary: LanguageUtil.get({ id: 'common.label.store', defaultMessage: 'Store' }),

        detail: LanguageUtil.get({ id: 'messages.success.undeployStore' }),
      });
      return true;
    } catch (e: any) {
      if (DeployErrorUtil.show(e, LanguageUtil.get({ id: 'common.label.store', defaultMessage: 'Store' }))) {
        return false;
      }
      const dependencies: any = e.response.data;
      if (dependencies?.message == 'Dependency Exception') {
        dispatch(openBulkActionDialogFromDependency(dependencies, ComponentName.STREAM));
        return false;
      }
      MessageUtil.error({ content: LanguageUtil.get({ id: 'messages.error.undeployStore' }) });
      return false;
    }
  }

  public async bulkAction(ids: string[], type: ActionType): Promise<BulkActionDependencyError[]> {
    const logs = await this.streamClient.bulkAction(ids, type);
    if (logs.some((item) => this.isColumnsEmptyBody(item.error))) {
      this.showColumnsEmpty();
    }
    return logs as BulkActionDependencyError[];
  }

  public warnIfColumnsMissing(columns: StreamColumnJsonModel[] | undefined): boolean {
    if (this.hasColumnContent(columns)) {
      return false;
    }
    this.showColumnsEmpty();
    return true;
  }

  private hasColumnContent(columns: StreamColumnJsonModel[] | undefined): boolean {
    if (columns == undefined || columns.length === 0) {
      return false;
    }
    return columns.every((column) => column.name != undefined && column.name.trim().length > 0 && !ExpressionUtil.isEmpty(column.expression));
  }

  private showColumnsEmpty(): void {
    MessageUtil.error({
      summary: LanguageUtil.get({ id: 'common.label.store', defaultMessage: 'Store' }),
      detail: LanguageUtil.get({ id: 'messages.error.columnsCannotBeEmpty', defaultMessage: 'columns cannot be empty' }),
    });
  }

  private isColumnsEmptyError(error: unknown): boolean {
    return this.isColumnsEmptyBody(this.readResponseBody(error));
  }

  private isColumnsEmptyBody(body: unknown): boolean {
    return this.readMessage(body) === 'columns cannot be empty';
  }

  private readResponseBody(error: unknown): unknown {
    if (typeof error !== 'object' || error == null || !('response' in error)) {
      return undefined;
    }
    const response = (error as { response?: { data?: unknown } }).response;
    return response?.data;
  }

  private readMessage(body: unknown): string | undefined {
    if (typeof body !== 'object' || body == null || !('message' in body)) {
      return undefined;
    }
    const message = (body as { message?: unknown }).message;
    return typeof message === 'string' ? message : undefined;
  }

  public async getCount(query: Query): Promise<number> {
    return await this.streamClient.getCount(query);
  }

  public async isExist(id: string): Promise<boolean> {
    return await this.streamClient.isExist(id);
  }

  private async parseExpression(syntax: Syntax) {
    const iterateSyntax = (syntax: Syntax): Syntax => {
      return {
        id: syntax.id,
        value: syntax.value,
        children: syntax.children?.map((c) => iterateSyntax(c)),
      };
    };
    return iterateSyntax(await this.expressionBridge.stop(syntax));
  }
}

export default StreamService;
